import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "../lib/api";
import Layout from "../components/Layout";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import {
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from "../components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import { toast } from "sonner";
import {
  todayISO,
  fmtDate,
  fmtNum,
  LOCATION_LABELS,
  ACCOUNTABLE_LOCATIONS,
} from "../lib/format";
import {
  canOperationalApprove,
  hasModuleAccess,
  hasRole,
  useAuth,
} from "../lib/auth";
import { useI18n } from "../lib/i18n";
import AssetClosing from "../components/AssetClosing";

const cls = (v) => (v === 0 ? "Matched" : v < 0 ? "Shortage" : "Excess");

export default function Closing() {
  const { user } = useAuth();
  const { t } = useI18n();
  const canWork =
    canOperationalApprove(user) &&
    hasModuleAccess(user, "store_inventory", "manage");
  const isAdmin = hasRole(user, "admin");
  const [outerTab, setOuterTab] = useState("linen");
  const [tab, setTab] = useState("new");
  const [items, setItems] = useState([]);
  const [closings, setClosings] = useState([]);

  const [closingDate, setClosingDate] = useState(todayISO());
  const [overallRemarks, setOverallRemarks] = useState("");
  const [location, setLocation] = useState("");
  const [rows, setRows] = useState([]); // {item_id,name,size,category,system_qty}
  const [asOf, setAsOf] = useState(null);
  const [snapshot, setSnapshot] = useState({}); // item_id -> system qty at load time
  const [physical, setPhysical] = useState({}); // item_id -> value
  const [remarks, setRemarks] = useState({}); // item_id -> remark
  const [unlisted, setUnlisted] = useState([]); // {item_id,name,qty,explanation}
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("all");
  const [draftId, setDraftId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [detail, setDetail] = useState(null);
  const [unlistedOpen, setUnlistedOpen] = useState(false);
  const [ul, setUl] = useState({ item_id: "", qty: "", explanation: "" });

  const load = async () => {
    const [i, c] = await Promise.all([api.get("/items"), api.get("/closings")]);
    setItems(i.data);
    setClosings(c.data);
  };
  useEffect(() => {
    load();
  }, []);

  const linenLocations = ACCOUNTABLE_LOCATIONS;
  const itemName = (id) => items.find((x) => x.id === id)?.name || id;

  const loadLocation = async (loc) => {
    setLocation(loc);
    setPhysical({});
    setRemarks({});
    setUnlisted([]);
    setSearch("");
    setCategory("all");
    setDraftId(null);
    try {
      const { data } = await api.get(`/closings/location-balance`, {
        params: { location: loc },
      });
      setRows(data.rows);
      setAsOf(data.as_of);
      setSnapshot(
        Object.fromEntries(data.rows.map((r) => [r.item_id, r.system_qty])),
      );
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
      setRows([]);
    }
  };

  const categories = useMemo(
    () => Array.from(new Set(rows.map((r) => r.category).filter(Boolean))),
    [rows],
  );
  const visibleRows = useMemo(
    () =>
      rows.filter(
        (r) =>
          r.name.toLowerCase().includes(search.toLowerCase()) &&
          (category === "all" || r.category === category),
      ),
    [rows, search, category],
  );

  const varianceOf = (r) =>
    physical[r.item_id] === "" || physical[r.item_id] == null
      ? null
      : Number(physical[r.item_id]) - r.system_qty;
  const summary = useMemo(() => {
    let counted = 0,
      matched = 0,
      sh = 0,
      ex = 0,
      sysUnits = 0;
    rows.forEach((r) => {
      sysUnits += r.system_qty;
      const v = varianceOf(r);
      if (v != null) {
        counted++;
        if (v === 0) matched++;
        else if (v < 0) sh++;
        else ex++;
      }
    });
    unlisted.forEach(() => {
      ex++;
      counted++;
    });
    return {
      types: rows.length + unlisted.length,
      sysUnits,
      counted,
      matched,
      sh,
      ex,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, physical, unlisted]);

  const buildLines = (draft) => {
    const lines = [];
    rows.forEach((r) => {
      const raw = physical[r.item_id];
      if (raw === "" || raw == null) {
        if (!draft) throw new Error(`Enter a physical count for ${r.name}`);
        else return;
      }
      if (!/^\d+$/.test(String(raw)))
        throw new Error(`${r.name}: whole numbers only`);
      const q = Number(raw);
      const v = q - r.system_qty;
      const rem = remarks[r.item_id];
      if (!draft && v !== 0 && (!rem || !rem.trim()))
        throw new Error(
          `Remark required for ${r.name} (variance ${v > 0 ? "+" : ""}${v})`,
        );
      lines.push({
        item_id: r.item_id,
        location,
        physical_qty: q,
        remark: rem || null,
      });
    });
    unlisted.forEach((u) =>
      lines.push({
        item_id: u.item_id,
        location,
        physical_qty: Number(u.qty),
        remark: u.explanation,
        unlisted: true,
      }),
    );
    return lines;
  };

  const save = async (draft) => {
    if (!location) {
      toast.error("Select a location");
      return;
    }
    let lines;
    try {
      lines = buildLines(draft);
    } catch (e) {
      toast.error(e.message);
      return;
    }
    if (!draft && lines.length === 0) {
      toast.error("Nothing to submit");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/closings", {
        closing_date: closingDate,
        location,
        inventory_type: "linen",
        lines,
        remarks: overallRemarks,
        is_draft: draft,
        draft_id: draftId,
        expected_as_of: asOf,
        system_snapshot: snapshot,
      });
      toast.success(draft ? "Draft saved" : "Closing submitted");
      if (draft) {
        setDraftId(data.id);
      } else {
        setTab("list");
        setLocation("");
        setRows([]);
      }
      await load();
    } catch (e) {
      if (e.response?.status === 409)
        toast.error(
          formatApiErrorDetail(e.response?.data?.detail) +
            " (refresh to reconcile)",
        );
      else toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const resumeDraft = async (c) => {
    setTab("new");
    setClosingDate(c.closing_date);
    setOverallRemarks(c.remarks || "");
    await loadLocation(c.location);
    const phys = {},
      rem = {};
    const uls = [];
    (c.lines || []).forEach((l) => {
      if (l.unlisted)
        uls.push({
          item_id: l.item_id,
          name: itemName(l.item_id),
          qty: l.physical_qty,
          explanation: l.remark || "",
        });
      else {
        phys[l.item_id] = String(l.physical_qty);
        if (l.remark) rem[l.item_id] = l.remark;
      }
    });
    setPhysical(phys);
    setRemarks(rem);
    setUnlisted(uls);
    setDraftId(c.id);
  };

  const approve = async (c) => {
    try {
      await api.post(`/closings/${c.id}/approve`, {});
      toast.success("Approved");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };
  const reject = async (c) => {
    const r = window.prompt("Reason for rejection?");
    if (!r) return;
    try {
      await api.post(`/closings/${c.id}/reject`, { reason: r });
      toast.success("Rejected");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };
  const reopen = async (c) => {
    const r = window.prompt("Reason for reopening this locked closing?");
    if (!r) return;
    try {
      await api.post(`/closings/${c.id}/reopen`, { reason: r });
      toast.success("Reopened for correction");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const addUnlisted = () => {
    if (
      !ul.item_id ||
      !/^\d+$/.test(String(ul.qty)) ||
      Number(ul.qty) <= 0 ||
      !ul.explanation.trim()
    ) {
      toast.error("Item, positive whole quantity and explanation are required");
      return;
    }
    setUnlisted([
      ...unlisted,
      {
        item_id: ul.item_id,
        name: itemName(ul.item_id),
        qty: Number(ul.qty),
        explanation: ul.explanation,
      },
    ]);
    setUl({ item_id: "", qty: "", explanation: "" });
    setUnlistedOpen(false);
  };

  const locName = (code) => LOCATION_LABELS[code] || code || "—";

  return (
    <Layout>
      <h1 className="section-title" data-testid="page-title">
        {t("nav.closing")}
      </h1>
      <div className="section-sub mb-4">{t("sub.closing")}</div>

      <Tabs value={outerTab} onValueChange={setOuterTab} className="mb-4">
        <TabsList data-testid="closing-outer-tabs">
          <TabsTrigger value="linen" data-testid="closing-tab-linen">
            Linen Closing
          </TabsTrigger>
          <TabsTrigger value="assets" data-testid="closing-tab-assets">
            Assets &amp; Consumables Closing
          </TabsTrigger>
        </TabsList>

        <TabsContent value="linen">
          <Tabs value={tab} onValueChange={setTab}>
            <TabsList className="mb-3">
              <TabsTrigger value="new" data-testid="tab-closing-new">
                New Closing
              </TabsTrigger>
              <TabsTrigger value="list" data-testid="tab-closing-list">
                History
              </TabsTrigger>
            </TabsList>

            <TabsContent value="new">
              {!canWork ? (
                <Card className="p-6 text-slate-600">
                  You have read-only access to closings.
                </Card>
              ) : (
                <>
                  <Card className="p-4 mb-4">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      <div>
                        <Label>Closing Date</Label>
                        <Input
                          type="date"
                          value={closingDate}
                          onChange={(e) => setClosingDate(e.target.value)}
                          className="h-11 mt-1"
                          data-testid="closing-date"
                        />
                      </div>
                      <div>
                        <Label>Location</Label>
                        <Select value={location} onValueChange={loadLocation}>
                          <SelectTrigger
                            className="h-11 mt-1"
                            data-testid="closing-location"
                          >
                            <SelectValue placeholder="Select a location" />
                          </SelectTrigger>
                          <SelectContent>
                            {linenLocations.map((l) => (
                              <SelectItem key={l} value={l}>
                                {locName(l)}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label>Overall Remarks</Label>
                        <Input
                          value={overallRemarks}
                          onChange={(e) => setOverallRemarks(e.target.value)}
                          className="h-11 mt-1"
                          data-testid="closing-remarks"
                        />
                      </div>
                    </div>
                  </Card>

                  {!location ? (
                    <Card
                      className="p-6 text-slate-500"
                      data-testid="closing-pick-location"
                    >
                      Select a location to load the items currently recorded
                      there.
                    </Card>
                  ) : rows.length === 0 && unlisted.length === 0 ? (
                    <Card
                      className="p-6 text-slate-500"
                      data-testid="closing-empty"
                    >
                      No inventory recorded at {locName(location)}.
                      <div className="mt-3">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setUnlistedOpen(true)}
                          data-testid="add-unlisted-btn"
                        >
                          Record Unlisted Item
                        </Button>
                      </div>
                    </Card>
                  ) : (
                    <>
                      <Card className="p-4 mb-3" data-testid="closing-summary">
                        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3 text-sm">
                          <div>
                            <div className="text-slate-500">Location</div>
                            <div className="font-semibold">
                              {locName(location)}
                            </div>
                          </div>
                          <div>
                            <div className="text-slate-500">Item types</div>
                            <div className="font-semibold">{summary.types}</div>
                          </div>
                          <div>
                            <div className="text-slate-500">System units</div>
                            <div className="font-semibold">
                              {fmtNum(summary.sysUnits)}
                            </div>
                          </div>
                          <div>
                            <div className="text-slate-500">Counted</div>
                            <div className="font-semibold">
                              {summary.counted}
                            </div>
                          </div>
                          <div>
                            <div className="text-slate-500">Matched</div>
                            <div className="font-semibold text-slate-700">
                              {summary.matched}
                            </div>
                          </div>
                          <div>
                            <div className="text-slate-500">Shortage</div>
                            <div className="font-semibold text-red-700">
                              {summary.sh}
                            </div>
                          </div>
                          <div>
                            <div className="text-slate-500">Excess</div>
                            <div className="font-semibold text-emerald-700">
                              {summary.ex}
                            </div>
                          </div>
                        </div>
                        <div className="text-xs text-slate-400 mt-2">
                          Expected quantity as of {fmtDate(asOf)}
                        </div>
                      </Card>

                      <div className="flex items-center gap-2 mb-2 flex-wrap">
                        <Input
                          placeholder="Search item…"
                          value={search}
                          onChange={(e) => setSearch(e.target.value)}
                          className="h-9 w-52"
                          data-testid="closing-search"
                        />
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setUnlistedOpen(true)}
                          data-testid="add-unlisted-btn"
                        >
                          Record Unlisted Item
                        </Button>
                      </div>

                      <Card className="p-0 overflow-hidden">
                        <div className="overflow-x-auto">
                          <table
                            className="vr-table"
                            data-testid="closing-form-table"
                          >
                            <thead>
                              <tr>
                                <th>Item</th>
                                <th className="num">System Qty</th>
                                <th className="num">Physical Count</th>
                                <th className="num">Variance</th>
                                <th>Remark</th>
                              </tr>
                            </thead>
                            <tbody>
                              {visibleRows.map((r) => {
                                const v = varianceOf(r);
                                return (
                                  <tr key={r.item_id}>
                                    <td>
                                      {r.name}
                                      {r.unlisted ? " · unlisted" : ""}
                                    </td>
                                    <td className="num">
                                      {fmtNum(r.system_qty)}
                                    </td>
                                    <td className="num">
                                      <Input
                                        type="number"
                                        min="0"
                                        step="1"
                                        placeholder=""
                                        value={physical[r.item_id] ?? ""}
                                        onChange={(e) =>
                                          setPhysical({
                                            ...physical,
                                            [r.item_id]: e.target.value,
                                          })
                                        }
                                        className="h-9 w-24 ml-auto text-right"
                                        data-testid={`phys-${r.name}`
                                          .replace(/\s+/g, "-")
                                          .toLowerCase()}
                                      />
                                    </td>
                                    <td
                                      className={`num ${v == null ? "text-slate-300" : v === 0 ? "" : v < 0 ? "text-red-700" : "text-emerald-700"}`}
                                    >
                                      {v == null ? "—" : v > 0 ? `+${v}` : v}
                                    </td>
                                    <td>
                                      <Input
                                        value={remarks[r.item_id] || ""}
                                        onChange={(e) =>
                                          setRemarks({
                                            ...remarks,
                                            [r.item_id]: e.target.value,
                                          })
                                        }
                                        placeholder={
                                          v != null && v !== 0
                                            ? "required"
                                            : "optional"
                                        }
                                        className="h-9"
                                        data-testid={`rem-${r.name}`
                                          .replace(/\s+/g, "-")
                                          .toLowerCase()}
                                      />
                                    </td>
                                  </tr>
                                );
                              })}
                              {unlisted.map((u, i) => (
                                <tr
                                  key={`ul-${i}`}
                                  className="bg-emerald-50/40"
                                >
                                  <td>
                                    {u.name}{" "}
                                    <span className="text-xs text-emerald-700">
                                      · unlisted
                                    </span>
                                  </td>
                                  <td className="num">0</td>
                                  <td className="num">{fmtNum(u.qty)}</td>
                                  <td className="num text-emerald-700">
                                    +{u.qty}
                                  </td>
                                  <td className="text-xs">{u.explanation}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </Card>

                      <div className="mt-4 flex gap-2">
                        <Button
                          variant="outline"
                          onClick={() => save(true)}
                          disabled={busy}
                          data-testid="closing-draft"
                        >
                          Save as Draft
                        </Button>
                        <Button
                          className="h-11 bg-slate-900 text-white hover:bg-slate-800 px-8"
                          onClick={() => save(false)}
                          disabled={busy}
                          data-testid="closing-submit"
                        >
                          Submit Closing
                        </Button>
                      </div>
                    </>
                  )}
                </>
              )}
            </TabsContent>

            <TabsContent value="list">
              <Card className="p-0 overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="vr-table" data-testid="closings-history">
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Location</th>
                        <th>Type</th>
                        <th>Expected as of</th>
                        <th>Status</th>
                        <th>Counted by</th>
                        <th>Submitted</th>
                        <th className="num">Matched</th>
                        <th className="num">Short</th>
                        <th className="num">Excess</th>
                        <th className="no-print">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {closings.map((c) => (
                        <tr key={c.id}>
                          <td>{c.closing_date}</td>
                          <td>{locName(c.location)}</td>
                          <td className="capitalize">
                            {c.inventory_type || "linen"}
                          </td>
                          <td className="text-xs">
                            {c.expected_as_of ? fmtDate(c.expected_as_of) : "—"}
                          </td>
                          <td>
                            <Badge
                              className={
                                c.status === "approved"
                                  ? "bg-emerald-100 border text-emerald-800"
                                  : c.status === "rejected"
                                    ? "bg-red-100 border text-red-800"
                                    : c.status === "draft" ||
                                        c.status === "reopened"
                                      ? "bg-slate-100 border text-slate-700"
                                      : "bg-amber-100 border text-amber-800"
                              }
                            >
                              {c.status}
                            </Badge>
                          </td>
                          <td>
                            {c.counted_by_name || c.submitted_by_name || "—"}
                          </td>
                          <td className="text-xs">
                            {c.submitted_at ? fmtDate(c.submitted_at) : "—"}
                          </td>
                          <td className="num">{c.matched_count ?? "—"}</td>
                          <td className="num text-red-700">
                            {c.shortage_count ?? "—"}
                          </td>
                          <td className="num text-emerald-700">
                            {c.excess_count ?? "—"}
                          </td>
                          <td className="no-print flex flex-wrap gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => setDetail(c)}
                              data-testid={`details-${c.id}`}
                            >
                              View Details
                            </Button>
                            {canWork &&
                              (c.status === "draft" ||
                                c.status === "reopened") && (
                                <Button
                                  size="sm"
                                  onClick={() => resumeDraft(c)}
                                  data-testid={`resume-${c.id}`}
                                >
                                  Resume Draft
                                </Button>
                              )}
                            {canWork && c.status === "submitted" && (
                              <>
                                <Button
                                  size="sm"
                                  onClick={() => approve(c)}
                                  data-testid={`approve-${c.id}`}
                                >
                                  Approve
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => reject(c)}
                                  data-testid={`reject-${c.id}`}
                                >
                                  Reject
                                </Button>
                              </>
                            )}
                            {isAdmin &&
                              (c.status === "submitted" ||
                                c.status === "approved" ||
                                c.status === "rejected") && (
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => reopen(c)}
                                  data-testid={`reopen-${c.id}`}
                                >
                                  Reopen
                                </Button>
                              )}
                          </td>
                        </tr>
                      ))}
                      {closings.length === 0 && (
                        <tr>
                          <td
                            colSpan={11}
                            className="text-center text-slate-500 py-5"
                          >
                            No closings yet.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </Card>
            </TabsContent>
          </Tabs>
        </TabsContent>

        <TabsContent value="assets">
          <AssetClosing />
        </TabsContent>
      </Tabs>

      {/* Unlisted item dialog */}
      <Dialog open={unlistedOpen} onOpenChange={setUnlistedOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Record Unlisted Item (Excess)</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Item</Label>
              <Select
                value={ul.item_id}
                onValueChange={(v) => setUl({ ...ul, item_id: v })}
              >
                <SelectTrigger
                  className="h-10 mt-1"
                  data-testid="unlisted-item"
                >
                  <SelectValue placeholder="Select item" />
                </SelectTrigger>
                <SelectContent>
                  {items
                    .filter(
                      (i) =>
                        i.active !== false &&
                        !rows.some((r) => r.item_id === i.id),
                    )
                    .map((i) => (
                      <SelectItem key={i.id} value={i.id}>
                        {i.name}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Physical Quantity Found</Label>
              <Input
                type="number"
                min="1"
                step="1"
                value={ul.qty}
                onChange={(e) => setUl({ ...ul, qty: e.target.value })}
                className="h-10 mt-1"
                data-testid="unlisted-qty"
              />
            </div>
            <div>
              <Label>Explanation (required)</Label>
              <Input
                value={ul.explanation}
                onChange={(e) => setUl({ ...ul, explanation: e.target.value })}
                className="h-10 mt-1"
                data-testid="unlisted-explanation"
              />
            </div>
            <div className="text-xs text-slate-500">
              Recorded as an Excess discrepancy — it does not change stock until
              Admin approves an audited adjustment.
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setUnlistedOpen(false)}>
              Cancel
            </Button>
            <Button
              className="bg-slate-900 text-white hover:bg-slate-800"
              onClick={addUnlisted}
              data-testid="unlisted-add"
            >
              Add
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View details */}
      <Dialog open={!!detail} onOpenChange={() => setDetail(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              Closing · {detail && locName(detail.location)} ·{" "}
              {detail?.closing_date}
            </DialogTitle>
          </DialogHeader>
          {detail && (
            <div className="space-y-2 text-sm">
              <div className="text-slate-600">
                Counted by{" "}
                <b>
                  {detail.counted_by_name || detail.submitted_by_name || "—"}
                </b>{" "}
                · Expected as of{" "}
                {detail.expected_as_of ? fmtDate(detail.expected_as_of) : "—"} ·
                Status {detail.status}
                {detail.reopen_reason
                  ? ` · Reopen reason: ${detail.reopen_reason}`
                  : ""}
              </div>
              <table className="vr-table">
                <thead>
                  <tr>
                    <th>Item</th>
                    <th className="num">System</th>
                    <th className="num">Physical</th>
                    <th className="num">Variance</th>
                    <th>Class</th>
                    <th>Remark</th>
                  </tr>
                </thead>
                <tbody>
                  {(detail.lines || []).map((l, i) => (
                    <tr key={`${l.item_id || "x"}-${i}`}>
                      <td>
                        {itemName(l.item_id)}
                        {l.unlisted ? " · unlisted" : ""}
                      </td>
                      <td className="num">{fmtNum(l.expected_qty)}</td>
                      <td className="num">{fmtNum(l.physical_qty)}</td>
                      <td className="num">
                        {l.variance > 0 ? `+${l.variance}` : l.variance}
                      </td>
                      <td>{l.classification || cls(l.variance)}</td>
                      <td>{l.remark || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDetail(null)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
